import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Paper, 
    Typography, 
    Chip, 
    Button,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TablePagination,
    Skeleton,
    IconButton,
    TableSortLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import { RestaurantService } from '../../../api/services/restaurantService';
import CreateOrderView from './CreateOrderView';
import OrderDetailsDrawer from './OrderDetailsDrawer';
import CancelOrderModal from './CancelOrderModal';
import ReplayIcon from '@mui/icons-material/Replay';
import { formatCurrency } from '../../../utils/currency';
import { extractTotalCount, standardFilterContainerSx, standardSearchInputSx, standardFilterSelectSx, standardFilterInputSx, standardResetButtonSx } from '../../../components/common/tableStyles';

export default function LiveOrdersTab() {
    const [view, setView] = useState('list'); // 'list' | 'create'
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    
    const [cancelModalOpen, setCancelModalOpen] = useState(false);
    const [orderToCancel, setOrderToCancel] = useState(null);

    // Pagination & Filters
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(20);
    const [totalRecords, setTotalRecords] = useState(0);

    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [dateFilter, setDateFilter] = useState('');
    const [sortField, setSortField] = useState('orderTime');
    const [sortOrder, setSortOrder] = useState('desc');

    // Debounce search
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchInput);
            setPage(0);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchInput]);

    const handleRequestSort = (property) => {
        const isAsc = sortField === property && sortOrder === 'asc';
        setSortOrder(isAsc ? 'desc' : 'asc');
        setSortField(property);
        setPage(0);
    };

    // Fetch data
    const fetchOrders = async () => {
        setLoading(true);
        try {
            const params = {
                page: page + 1,
                limit: limit,
                sort: sortField,
                order: sortOrder
            };
            if (debouncedSearch) params.search = debouncedSearch;
            if (statusFilter !== 'All') {
                if (statusFilter === 'Preparing') params.status = 'In Progress';
                else if (statusFilter === 'Served') params.status = 'Completed';
                else params.status = statusFilter;
            }
            if (dateFilter) params.date = dateFilter;

            const response = await RestaurantService.getRestaurantOrders(params);
            
            const dataArray = response?.data?.data || response?.data || response?.orders || [];
            setOrders(Array.isArray(dataArray) ? dataArray : []);
            setTotalRecords(extractTotalCount(response, dataArray));
        } catch (error) {
            console.error('Failed to fetch restaurant orders:', error);
            // Handled by global interceptor
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, [page, limit, debouncedSearch, statusFilter, dateFilter, sortField, sortOrder]);

    const getStatusColor = (status) => {
        switch (status) {
            case 'Served':
            case 'Completed': return 'success';
            case 'Ready': return 'info';
            case 'Preparing':
            case 'In Progress': return 'warning';
            case 'Pending': return 'primary';
            case 'Cancelled': return 'error';
            default: return 'default';
        }
    };

    const getDisplayStatus = (status) => {
        if (status === 'In Progress') return 'Preparing';
        if (status === 'Completed') return 'Served';
        return status;
    };

    const handleChangePage = (event, newPage) => setPage(newPage);
    
    const handleChangeRowsPerPage = (event) => {
        setLimit(parseInt(event.target.value, 10));
        setPage(0);
    };

    const formatDate = (isoString) => {
        if (!isoString) return 'N/A';
        return new Date(isoString).toLocaleString('en-US', { 
            year: 'numeric', month: 'short', day: 'numeric', 
            hour: '2-digit', minute: '2-digit' 
        });
    };

    const handleViewDetail = (orderId) => {
        setSelectedOrderId(orderId);
        setDrawerOpen(true);
    };

    const handleCancelClick = (orderId) => {
        setOrderToCancel(orderId);
        setCancelModalOpen(true);
    };

    if (view === 'create') {
        return (
            <CreateOrderView 
                onBack={() => setView('list')} 
                onSuccess={() => {
                    setView('list');
                    fetchOrders();
                }} 
            />
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button 
                    variant="contained" 
                    startIcon={<AddIcon sx={{ fontSize: 18 }} />} 
                    onClick={() => setView('create')}
                >
                    Create New Order
                </Button>
            </Box>

            {/* Filter Toolbar */}
            <Box sx={standardFilterContainerSx}>
                <TextField
                    size="small"
                    placeholder="Search by room or guest..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    sx={standardSearchInputSx}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#98a0aa', fontSize: 18 }} />
                            </InputAdornment>
                        ),
                    }}
                />
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <Select 
                            value={statusFilter} 
                            displayEmpty
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setPage(0);
                            }}
                            sx={standardFilterSelectSx}
                        >
                            <MenuItem value="All">All Statuses</MenuItem>
                            <MenuItem value="Pending">Pending</MenuItem>
                            <MenuItem value="Preparing">Preparing</MenuItem>
                            <MenuItem value="Ready">Ready</MenuItem>
                            <MenuItem value="Served">Served</MenuItem>
                            <MenuItem value="Cancelled">Cancelled</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        size="small"
                        type="date"
                        value={dateFilter}
                        onChange={(e) => {
                            setDateFilter(e.target.value);
                            setPage(0);
                        }}
                        sx={{ minWidth: 160, ...standardFilterInputSx }}
                    />
                    {(searchInput !== '' || statusFilter !== 'All' || dateFilter !== '') && (
                        <Button 
                            variant="outlined" 
                            startIcon={<ReplayIcon sx={{ fontSize: 16 }} />}
                            onClick={() => {
                                setSearchInput('');
                                setStatusFilter('All');
                                setDateFilter('');
                                setPage(0);
                            }}
                            sx={standardResetButtonSx}
                        >
                            Reset
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Data Grid */}
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Ticket #</TableCell>
                            <TableCell>Assigned Room / Folio</TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={sortField === 'orderTime'}
                                    direction={sortField === 'orderTime' ? sortOrder : 'asc'}
                                    onClick={() => handleRequestSort('orderTime')}
                                >
                                    Placement Time
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>Items</TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={sortField === 'totalAmount'}
                                    direction={sortField === 'totalAmount' ? sortOrder : 'asc'}
                                    onClick={() => handleRequestSort('totalAmount')}
                                >
                                    Total Amount
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>Workflow Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            Array.from(new Array(limit)).map((_, i) => (
                                <TableRow key={`skeleton-${i}`}>
                                    <TableCell><Skeleton width="40%" /></TableCell>
                                    <TableCell><Skeleton width="60%" /><Skeleton width="30%" height={16} /></TableCell>
                                    <TableCell><Skeleton width="70%" /></TableCell>
                                    <TableCell><Skeleton width="50%" /></TableCell>
                                    <TableCell><Skeleton width="40%" /></TableCell>
                                    <TableCell><Skeleton width={80} sx={{ display: 'inline-block' }} /></TableCell>
                                    <TableCell align="right"><Skeleton width={32} sx={{ display: 'inline-block' }} /></TableCell>
                                </TableRow>
                            ))
                        ) : orders.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                                    <Typography color="text.secondary">No restaurant orders found matching the current filters.</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            orders.map((order) => (
                                <TableRow key={order.id} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold">
                                            {order.orderId || order.ticketNumber || order.id.substring(0, 8).toUpperCase()}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold">
                                            {order.room?.roomNumber ? `Room ${order.room.roomNumber}` : 'Direct Sale'}
                                        </Typography>
                                        {order.guest?.name && (
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Guest: {order.guest.name}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {formatDate(order.orderTime || order.createdAt || order.placedAt)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography 
                                            variant="body2" 
                                            color="text.secondary"
                                            sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                        >
                                            {(() => {
                                                const items = order.items || order.orderItems;
                                                if (items && items.length > 0) {
                                                    return items.map(i => i.menuItem?.name || i.name || 'Item').join(', ');
                                                }
                                                return order.totalItems ? `${order.totalItems} items` : 'N/A';
                                            })()}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold">
                                            {formatCurrency(order.totalAmount || order.totalPrice || order.total || 0)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={getDisplayStatus(order.status || 'Pending')} 
                                            size="small" 
                                            color={getStatusColor(order.status || 'Pending')} 
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton 
                                            size="small" 
                                            color="primary"
                                            onClick={() => handleViewDetail(order.id)}
                                        >
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                        {(order.status === 'Pending' || order.status === 'Preparing' || order.status === 'In Progress') && (
                                            <IconButton 
                                                size="small" 
                                                color="error"
                                                onClick={() => handleCancelClick(order.id)}
                                            >
                                                <DoNotDisturbIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                
                {/* Pagination Controls */}
                <TablePagination
                    component="div"
                    count={orders.length === 0 ? 0 : totalRecords}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={limit}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[20, 50, 100]}
                />
            </TableContainer>

            <OrderDetailsDrawer 
                open={drawerOpen} 
                onClose={() => {
                    setDrawerOpen(false);
                    setSelectedOrderId(null);
                }} 
                orderId={selectedOrderId} 
                onStatusUpdated={fetchOrders}
            />

            <CancelOrderModal
                open={cancelModalOpen}
                onClose={() => {
                    setCancelModalOpen(false);
                    setOrderToCancel(null);
                }}
                orderId={orderToCancel}
                onSuccess={() => {
                    setCancelModalOpen(false);
                    setOrderToCancel(null);
                    fetchOrders();
                }}
            />
        </Box>
    );
}
